import { useEffect, useState } from 'react';

interface CookieBannerProps {
  visible: boolean;
  cookiesPolicyUrl: string;
  ui?: {
    banner?: string;
    policyLink?: string;
    acceptAll?: string;
    rejectAll?: string;
    customize?: string;
    dialogLabel?: string;
  };
  onAcceptAll: () => void;
  onRejectAll: () => void;
  onCustomize: () => void;
}

/**
 * Bottom-fixed GDPR consent banner (non-blocking overlay).
 */
export function CookieBanner({
  visible,
  cookiesPolicyUrl,
  ui = {},
  onAcceptAll,
  onRejectAll,
  onCustomize,
}: CookieBannerProps) {
  const [mounted, setMounted] = useState(false);
  const [leaving, setLeaving] = useState(false);

  useEffect(() => {
    if (visible) {
      setLeaving(false);
      const t = requestAnimationFrame(() => setMounted(true));
      return () => cancelAnimationFrame(t);
    }
    if (mounted) {
      setLeaving(true);
      const t = window.setTimeout(() => {
        setMounted(false);
        setLeaving(false);
      }, 280);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [visible, mounted]);

  if (!visible && !mounted) {
    return null;
  }

  return (
    <div
      className={`cookie-banner ${mounted && !leaving ? 'cookie-banner--visible' : 'cookie-banner--hidden'}`}
      role="dialog"
      aria-label={ui.dialogLabel ?? 'Cookie consent'}
      aria-live="polite"
    >
      <div className="cookie-banner__inner">
        <p className="cookie-banner__text">
          {ui.banner ?? 'We use cookies to improve your experience.'}{' '}
          <a href={cookiesPolicyUrl} className="cookie-banner__link">
            {ui.policyLink ?? 'Cookie Policy'}
          </a>
        </p>
        <div className="cookie-banner__actions">
          <button type="button" className="lux-btn-secondary cookie-banner__btn" onClick={onCustomize}>
            {ui.customize ?? 'Customize'}
          </button>
          <button type="button" className="lux-btn-secondary cookie-banner__btn" onClick={onRejectAll}>
            {ui.rejectAll ?? 'Reject optional'}
          </button>
          <button type="button" className="lux-btn-primary cookie-banner__btn" onClick={onAcceptAll}>
            {ui.acceptAll ?? 'Accept all'}
          </button>
        </div>
      </div>
    </div>
  );
}
